import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { config } from '../config';

const API_URL = config.apiUrl;

interface ErrorResponse {
  error: string;
  message?: string;
  retryAfterSeconds?: number;
  issues?: Array<{ message: string }>;
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (!lockedUntil) {
      setCountdown(0);
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.floor((lockedUntil.getTime() - Date.now()) / 1000));
      setCountdown(remaining);

      if (remaining <= 0) {
        setLockedUntil(null);
        setStatus('idle');
        setMessage('');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Don't allow requests while rate limited
    if (lockedUntil && lockedUntil.getTime() > Date.now()) {
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json() as ErrorResponse;

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'If an account exists with this email, you will receive a password reset link.');
      } else {
        // Handle rate limiting (429)
        if (response.status === 429 && data.retryAfterSeconds) {
          setLockedUntil(new Date(Date.now() + data.retryAfterSeconds * 1000));
          setStatus('error');
          setMessage('Too many requests. Please wait before trying again.');
          return;
        }

        setStatus('error');
        // Handle both string errors and ZodError objects
        let errorMessage = 'Something went wrong. Please try again.';
        if (typeof data?.error === 'string') {
          errorMessage = data.error;
        } else if (data?.issues?.[0]?.message) {
          // ZodError format
          errorMessage = data.issues[0].message;
        }
        setMessage(errorMessage);
      }
    } catch {
      setStatus('error');
      setMessage('Unable to connect to server. Please try again.');
    }
  };

  const isRateLimited = Boolean(lockedUntil && lockedUntil.getTime() > Date.now());

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-16 w-16 bg-primary-500 rounded-2xl flex items-center justify-center">
            <span className="text-3xl font-bold text-white">C</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        {status === 'success' ? (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-4 rounded-lg">
              <p className="font-medium">Check your email</p>
              <p className="mt-1 text-sm">{message}</p>
            </div>
            <Link
              to="/login"
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to login
            </Link>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {/* Rate limit countdown */}
            {isRateLimited && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Please wait</span>
                </div>
                <p className="text-sm">Too many password reset requests. You can try again in:</p>
                <div className="text-2xl font-mono font-bold text-center mt-2">
                  {formatCountdown(countdown)}
                </div>
              </div>
            )}

            {status === 'error' && !isRateLimited && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {message}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isRateLimited}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="you@example.com"
              />
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={status === 'loading' || isRateLimited}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? 'Sending...' : isRateLimited ? 'Please wait' : 'Send reset link'}
              </button>

              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
